// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useState } from 'react';

import type { TaskTypeGroupId } from '@evolvemep/api-ts';
import { Accordion } from '@mantine/core';

import { isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';

import { DefinedTaskGroup } from './DefinedTaskGroup';
import { undefinedGroupKey, UndefinedTaskTypeGroupSection } from './UndefinedTaskTypeGroupSection';
import { useTaskTypes } from './useTaskTypes';

export type TaskTypeGroupsSelectProps = {
  hideGroupSelect?: boolean;
  hideDelete?: boolean;
  isWorkCellMapping?: boolean;
};

export const TaskTypeGroupsSelect = ({
  hideGroupSelect = false,
  hideDelete,
  isWorkCellMapping,
}: TaskTypeGroupsSelectProps) => {
  const { searchText, groupsPaginatedGet, shopConfiguration } = useTaskTypes();

  const [openItems, setOpenItems] = useState<(TaskTypeGroupId | typeof undefinedGroupKey)[]>([]);

  const {
    data: groups,
    entireCount,
    loading: loadingGroups,
    fetchNextPage: fetchNextPageOfGroups,
    setDefaultOpts,
  } = groupsPaginatedGet;

  useEffect(() => {
    if (searchText) {
      setOpenItems([...groups.map((g) => g.taskTypeGroupId), undefinedGroupKey]);
    }
  }, [groups, searchText]);

  useEffect(() => {
    setDefaultOpts({
      defaultConfig: {
        params: {
          name: searchText,
          searchForTasks: isNotNil(searchText),
          shopConfigurationId: shopConfiguration?.shopConfigurationId,
        },
      },
    });
  }, [searchText, setDefaultOpts, shopConfiguration?.shopConfigurationId]);

  const openUndefinedGroupCheck = useCallback(() => {
    if (!loadingGroups && entireCount === 0) {
      setOpenItems([undefinedGroupKey]);
    }
  }, [entireCount, loadingGroups]);

  return (
    <Accordion
      multiple
      value={openItems}
      onChange={(newValues) => setOpenItems(newValues as typeof openItems)}
      style={{
        flex: '1 0 0',
        overflowY: 'auto',
      }}
      styles={{
        item: { borderBottom: 0 },
        control: { backgroundColor: 'var(--mantine-color-gray-2)' },
        content: { padding: 0 },
      }}
    >
      {/*
        Accordion MUST be visible before setting open items
        when accordion is visible, trigger logic to auto-expand undefined if no groups.
      */}
      <TriggerOnVisible onVisible={openUndefinedGroupCheck} />

      {groups.map((g) => (
        <DefinedTaskGroup
          key={g.taskTypeGroupId}
          taskTypeGroup={g}
          expanded={openItems.some((o) => o === g.taskTypeGroupId)}
          hideGroupSelect={hideGroupSelect}
          hideDelete={hideDelete}
          isWorkCellMapping={isWorkCellMapping}
        />
      ))}
      {groups.length === entireCount && <UndefinedTaskTypeGroupSection hideDelete={hideDelete} />}

      <TriggerOnVisible onVisible={fetchNextPageOfGroups} loading={loadingGroups} />
    </Accordion>
  );
};
